import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { InfoOption } from '@app/forms/subscription/components/info/info.component';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { SupportsTable } from '../../shared/supports-table/supports-table.model';
import {
  FULL_AMOUNT_TOLERANCE,
  FULL_PERCENT_TOLERANCE,
  roundTo,
  toNumber,
} from '../../versements-libres/services/versement-allocation';
import {
  amountsFromShares,
  sharesFromWeights,
  totalOf,
} from '../services/rachat-allocation';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

const HISTORICAL_TABLE: SupportsTable = {
  title: 'Supports à désinvestir',
  totalLabel: 'Total désinvesti',
  emptyLabel: 'Aucun support à désinvestir.',
  columns: [
    { label: 'Support', type: 'support' },
    {
      label: 'Valorisation',
      type: 'amount',
      valueName: 'currentAllocationAmount',
      unit: '€',
      digits: '1.4-4',
      total: true,
    },
    {
      label: 'En %',
      type: 'input',
      controlName: 'disinvest',
      unit: '%',
      step: '0.0001',
      min: 0,
      max: 100,
      ariaLabel: 'Désinvestissement en pourcentage',
    },
    {
      label: 'En €',
      type: 'input',
      controlName: 'amountDisinvest',
      unit: '€',
      digits: '1.4-4',
      step: '0.0001',
      min: 0,
      max: row => Number(row.get('currentAllocationAmount')?.value) || 0,
      ariaLabel: 'Désinvestissement en euros',
      total: true,
    },
  ],
};

@UntilDestroy()
@Component({
  selector: 'rachat-disinvestment-form',
  templateUrl: './rachat-disinvestment-form.component.html',
  styleUrls: ['./rachat-disinvestment-form.component.scss'],
  standalone: false,
})
export class RachatDisinvestmentFormComponent
  extends EaSubscriptionBuilder
  implements OnInit, OnDestroy
{
  optionInfo: InfoOption = { icon: 'info' };
  optionWarning: InfoOption = { icon: 'warning' };

  private destroy$ = new Subject<void>();
  // Libellés français des types de support (référentiel), indexés par id (ex. 'STRUCTURED_PRODUCTS')
  // — utilisé par supportsTable.getTypeLabel pour éviter d'afficher le code technique brut à côté
  // de l'isin dans la colonne "Support" (cf. supports-table.component.ts:supportDetail()).
  private supportTypeLabels: Record<string, string> = {};

  constructor(
    protected formConstructor: PrFormConstructor,
    protected subscriptionFacade: SubscriptionFacadeService,
    protected injector: Injector,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);
  }

  protected get formOptions(): any {
    return {};
  }

  ngOnInit() {
    this.subscriptionFacade.initForm(this, this.formOptions);

    this.referenceDao
      .getSupportTypes()
      .pipe(untilDestroyed(this))
      .subscribe(types => {
        this.supportTypeLabels = Object.fromEntries(
          (types || []).map((type: any) => [type.id, type.value]),
        );
      });
  }

  getSupportTypeLabel(typeId: string): string {
    return this.supportTypeLabels[typeId] || typeId;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(entity: any, options?: any) {
    super.initForm(entity, options);

    if (this.amountDriven) this.attachRachatSync(this.destroy$);
    else this.attachDivestmentSync(this.destroy$);
  }

  get supportsTable(): SupportsTable {
    const getTypeLabel = (typeId: string) => this.getSupportTypeLabel(typeId);

    if (!this.amountDriven) return { ...HISTORICAL_TABLE, getTypeLabel };

    return {
      title: 'Répartition du rachat entre les supports',
      totalLabel: 'Total réparti',
      emptyLabel: 'Aucun support à désinvestir.',
      targetPercent: 100,
      remainingAmount: this.rachatAmount > 0 ? this.remainingAmount : undefined,
      getTypeLabel,
      columns: [
        { label: 'Support', type: 'support' },
        {
          label: 'Valorisation',
          type: 'amount',
          valueName: 'currentAllocationAmount',
          unit: '€',
          digits: '1.2-2',
          total: true,
        },
        {
          label: 'Part du rachat',
          type: 'input',
          controlName: 'disinvest',
          unit: '%',
          step: '0.0001',
          min: 0,
          max: row => this.maxShareOf(row),
          ariaLabel: 'Part du rachat en pourcentage',
          total: true,
        },
        {
          label: 'En €',
          type: 'input',
          controlName: 'amountDisinvest',
          unit: '€',
          digits: '1.2-2',
          step: '0.01',
          min: 0,
          max: row => this.maxAmountOf(row),
          ariaLabel: 'Désinvestissement en euros',
          total: true,
        },
      ],
    };
  }

  get rachatAmount(): number {
    return toNumber(this.rachatAmountControl?.value);
  }

  get contractTotalAmount(): number {
    return toNumber(
      this.entity?.products?.[0]?.parameters?.currentSupportRepartition
        ?.totalAmount,
    );
  }

  get listedValorisation(): number {
    return totalOf(
      this.rows.map(row => toNumber(row.get('currentAllocationAmount')?.value)),
    );
  }

  get hasNoSupport(): boolean {
    return !this.rows.length;
  }

  get hasTruncatedSupports(): boolean {
    const contractTotal = this.contractTotalAmount;
    if (!(contractTotal > 0)) return false;

    return contractTotal - this.listedValorisation > FULL_AMOUNT_TOLERANCE;
  }

  get exceedsListedValorisation(): boolean {
    return (
      this.rachatAmount > 0 &&
      this.rachatAmount - this.listedValorisation > FULL_AMOUNT_TOLERANCE
    );
  }

  get totalSharePercent(): number {
    return roundTo(
      totalOf(this.rows.map(row => toNumber(row.get('disinvest')?.value))),
      4,
    );
  }

  get totalDisinvestedAmount(): number {
    return roundTo(
      totalOf(
        this.rows.map(row => toNumber(row.get('amountDisinvest')?.value)),
      ),
      2,
    );
  }

  get remainingPercent(): number {
    return roundTo(100 - this.totalSharePercent, 4);
  }

  get remainingAmount(): number {
    return roundTo(this.rachatAmount - this.totalDisinvestedAmount, 2);
  }

  get isRachatFullyAllocated(): boolean {
    if (!(this.rachatAmount > 0)) return false;

    return (
      Math.abs(this.remainingPercent) < FULL_PERCENT_TOLERANCE &&
      Math.abs(this.remainingAmount) < FULL_AMOUNT_TOLERANCE
    );
  }

  repartirAuProrata() {
    const valorisations = this.rows.map(row =>
      toNumber(row.get('currentAllocationAmount')?.value),
    );
    if (!(totalOf(valorisations) > 0) || !(this.rachatAmount > 0)) return;
    if (this.exceedsListedValorisation) return;

    const shares = sharesFromWeights(valorisations);
    const amounts = amountsFromShares(shares, this.rachatAmount);

    this.rows.forEach((row, index) => {
      row.get('disinvest').setValue(shares[index], { emitEvent: false });
      row.get('amountDisinvest').setValue(amounts[index], { emitEvent: false });
      row.get('amountDisinvest').updateValueAndValidity({ emitEvent: false });
    });

    this.form.markAsDirty();
  }

  onSubmit() {
    this.form.markAsDirty();
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  disabledNextBtn() {
    if (this.amountDriven)
      return this.form.invalid || !this.isRachatFullyAllocated;

    return this.hasNotDisinvestment();
  }

  hasNotDisinvestment() {
    const supportDivestments = this.supportsDivestment as UntypedFormArray;
    return !supportDivestments.controls.some(control => {
      return control.get('disinvest').value > 0;
    });
  }

  private get rows() {
    if (!this.products?.length) return [] as UntypedFormArray[];

    return (this.supportsDivestment?.controls || []) as UntypedFormArray[];
  }

  private maxShareOf(row): number {
    const valorisation = toNumber(row.get('currentAllocationAmount')?.value);
    const target = this.rachatAmount;
    if (!(valorisation > 0) || !(target > 0)) return 0;

    return Math.min(100, roundTo((valorisation / target) * 100, 4));
  }

  private maxAmountOf(row): number {
    const valorisation = toNumber(row.get('currentAllocationAmount')?.value);
    const target = this.rachatAmount;

    return target > 0 ? Math.min(valorisation, target) : valorisation;
  }
}
